// System
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Divider, Stack, MenuItem, IconButton, Box, Link, Button } from '@mui/material';

// Components
import { RHFMultiSelect, RHFSelect, RHFTextField } from '@Components/hook-form';
import { AtLocator } from '@Components/shared/locators/AtLocator';

// Hooks
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';
import { useProviderDrawer } from '@Modal/hooks/providers/useProviderDrawer';

// Graphql
import {
  // useGetAviatorRoundConfigsQuery,
  useGetProviderPublicKeyLazyQuery,
  useGetRetryConfigsQuery,
} from '@Src/generated.graphql';
import { MenuItemEx } from '@Components/shared/selects/MenuItemEx';
import { useSetupProviderForm } from '@Src/shared/forms/providers/SetupProviderFormContext';
import { useWatch } from 'react-hook-form';

export const SetupProviderDetails: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { userPermissions } = useAuthorization();
  const { selectedProviderData, handleDeleteProviderConfigId } = useProviderDrawer();
  const [getProviderPublicKey, { data }] = useGetProviderPublicKeyLazyQuery();
  // const { data: aviatorRoundConfigsData } = useGetAviatorRoundConfigsQuery();
  const { data: retryConfigsData } = useGetRetryConfigsQuery();
  const { currentUser } = useAuthorization();
  const methods = useSetupProviderForm();

  const retryScheduledSendCredit = useWatch({
    control: methods.control,
    name: 'retryScheduledSendCredit',
  });

  const permissionToViewPublicKey =
    userPermissions?.includes('CAN_VIEW_PROVIDER_PUBLIC_KEY') || currentUser?.isSuper;

  // const demoUrl = 'https://client.crush.sigma-bet.com:81';
  // const documentation = 'https://docs.crush.theavigroup.com';
  // const currentUrl = window?.location?.origin;
  // const clientUrl = currentUrl?.replace('office.', '')?.replace('/providers', '');

  useEffect(() => {
    if (permissionToViewPublicKey && selectedProviderData?.id) {
      getProviderPublicKey({
        variables: {
          providerId: selectedProviderData.id,
        },
      });
    }
  }, [data]);

  const handleCopy = () => {
    if (data?.providerPublicKey) {
      navigator.clipboard.writeText(data?.providerPublicKey).then(() => {
        enqueueSnackbar('Public key copied', {
          autoHideDuration: 2000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
        });
      });
    }
  };

  const useDropDown = () => {
    // const gameVersionDropdown = useMemo(
    //   () =>
    //     aviatorRoundConfigsData?.aviatorRoundConfigs?.map(({ description, id }) => (
    //       <MenuItemEx key={id} value={id} name="Game Versions">
    //         {description}
    //       </MenuItemEx>
    //     )),
    //   [aviatorRoundConfigsData]
    // );
    const retryConfigDropdown = useMemo(
      () =>
        retryConfigsData?.retryConfigs?.map(({ name, _id }) => (
          // @ts-ignore
          <MenuItemEx key={_id} value={_id} name="Retry Configs">
            {name}
          </MenuItemEx>
        )),
      [retryConfigsData]
    );
    return {
      // gameVersion: gameVersionDropdown,
      retryConfig: retryConfigDropdown,
    };
  };
  const dropdown = useDropDown();

  return (
    <Stack direction="column" spacing={2} margin={2}>
      <AtLocator type="text-field" name="name" key="name">
        <RHFTextField name="name" label="Name" />
      </AtLocator>
      <RHFMultiSelect
        name="games"
        label="games"
        variant="outlined"
        chip
        options={[
          { label: 'Aviator', value: 1 },
          { label: 'Mines', value: 2 },
          { label: 'Rubber Duck', value: 3 },
        ]}
      ></RHFMultiSelect>
      <AtLocator type="text-field" name="apiUrl" key="apiUrl">
        <RHFTextField name="apiUrl" label="Api Url" />
      </AtLocator>
      <RHFSelect
        label="Status"
        name="status"
        variant="outlined"
        sx={{ width: '100%', marginTop: '6px', marginBottom: '15px' }}
      >
        <MenuItem value="ACTIVE">Active</MenuItem>
        <MenuItem value="INACTIVE">Inactive</MenuItem>
      </RHFSelect>
      <RHFSelect label="Is Internal" name="isInternal" variant="outlined">
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect
        label="Sync Round End?"
        name="syncRoundEnd"
        variant="outlined"
        sx={{ width: '100%', marginTop: '6px', marginBottom: '15px' }}
      >
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>

      <RHFSelect
        label="Retry Scheduled Send Credit"
        name="retryScheduledSendCredit"
        variant="outlined"
      >
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect
        label="Retry Config"
        name="retryConfigId"
        variant="outlined"
        disabled={!retryScheduledSendCredit}
      >
        {dropdown.retryConfig}
      </RHFSelect>
      <RHFSelect label="Refund On Credit Fail" name="refundOnCreditFail" variant="outlined">
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect
        label="Filter External Provider Bets"
        name="filterExternalProviderBets"
        variant="outlined"
      >
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>

      <RHFSelect label="Credit On Disconnect" name="creditOnDisconnect" variant="outlined">
        <MenuItem value="true">True</MenuItem>
        <MenuItem value="false">False</MenuItem>
      </RHFSelect>
      <RHFSelect label="Integration Type" name="integrationType" variant="outlined">
        <MenuItem value="DIRECT">DIRECT</MenuItem>
        <MenuItem value="REVERSE_BGI">REVERSE_BGI</MenuItem>
      </RHFSelect>
      {/* <Stack direction="row" gap={1}>
        <RHFSelect label="Game versions" name="configId" variant="outlined">
          {dropdown.gameVersion}
        </RHFSelect>
        {selectedProviderData?.configId !== null && (
          <Button variant="contained" color="error" onClick={() => handleDeleteProviderConfigId()}>
            Delete
          </Button>
        )}
      </Stack> */}
      {permissionToViewPublicKey && data?.providerPublicKey && (
        <>
          <Divider />
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ overflow: 'scroll' }}>
              <pre>{data?.providerPublicKey}</pre>
            </Box>
            <IconButton onClick={handleCopy}>
              <ContentCopyIcon />
            </IconButton>
          </Stack>
          {/* <Stack direction="row" spacing={2} alignItems="center">
            <Link href={demoUrl} target="_blank" rel="noreferrer">
              Open Demo
            </Link>
            <Link href={clientUrl} target="_blank" rel="noreferrer">
              Open Client
            </Link>
            <Link href={documentation} target="_blank" rel="noreferrer">
              Documentation
            </Link>
          </Stack> */}
        </>
      )}
    </Stack>
  );
};
